import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Plus } from "lucide-react";
import {
  ActivityEntry, DashboardSummary, Loan, RecentItem,
  dashboardApi, dueDescriptor, fileUrl, formatMoney, loansApi, timeAgo,
} from "@/lib/catalog-api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/catalog/Skeleton";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [recent, setRecent] = useState<RecentItem[] | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[] | null>(null);
  const [overdue, setOverdue] = useState<Loan[] | null>(null);

  useEffect(() => {
    dashboardApi.summary().then(setSummary).catch(() => setSummary({
      total_items: 0, items_out: 0, inventory_value: 0,
      clients_count: 0, locations_count: 0,
      active_loans: 0, overdue_loans: 0, outfits_count: 0,
    }));
    dashboardApi.recentItems().then(setRecent).catch(() => setRecent([]));
    dashboardApi.activity().then(setActivity).catch(() => setActivity([]));
    loansApi.list({ overdue: true }).then((rows) => setOverdue(rows.slice(0, 4))).catch(() => setOverdue([]));
  }, []);

  if (!user) return null;

  return (
    <main className="flex-1 px-6 md:px-10 py-10 md:py-14" data-testid="dashboard-page">
      <div className="max-w-6xl">
        <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-4">— Dashboard</p>
        <h1 data-testid="app-welcome-heading" className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-balance">
          Welcome back, {user.full_name.split(" ")[0]}.
        </h1>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border" data-testid="dashboard-stats">
          {summary ? [
            { label: "Total items", value: summary.total_items.toLocaleString(), tid: "stat-total-items" },
            { label: "On loan", value: summary.active_loans.toLocaleString(), tid: "stat-active-loans",
              hint: summary.overdue_loans > 0 ? `${summary.overdue_loans} overdue` : "All on time" },
            { label: "Inventory value", value: formatMoney(summary.inventory_value), tid: "stat-inventory-value" },
            { label: "Outfits built", value: summary.outfits_count.toLocaleString(), tid: "stat-outfits" },
          ].map((s) => (
            <div key={s.label} data-testid={s.tid} className="bg-background p-5 lg:p-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{s.label}</p>
              <p className="font-display text-3xl lg:text-4xl mt-2">{s.value}</p>
              {s.hint && <p className="text-[11px] text-muted-foreground mt-1">{s.hint}</p>}
            </div>
          )) : Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-background p-5 lg:p-6">
              <Skeleton className="h-3 w-24 mb-3" />
              <Skeleton className="h-9 w-32" />
            </div>
          ))}
        </div>

        {summary && summary.overdue_loans > 0 && overdue && overdue.length > 0 && (
          <section className="mt-8 border border-oxblood/30 bg-oxblood/5 p-5"
            data-testid="dashboard-overdue-widget">
            <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
              <p className="font-display text-lg flex items-center gap-2 text-oxblood-deep">
                <AlertTriangle className="w-4 h-4" /> {summary.overdue_loans} loan{summary.overdue_loans === 1 ? "" : "s"} overdue
              </p>
              <Link to="/app/loans?status=overdue" className="text-xs text-oxblood hover:underline underline-offset-4">
                Review all →
              </Link>
            </div>
            <ul className="space-y-2">
              {overdue.map((l) => {
                const d = dueDescriptor(l.due_at);
                return (
                  <li key={l._id} className="flex items-center gap-3 text-sm">
                    <span className="w-9 h-10 bg-bone overflow-hidden shrink-0">
                      {l.item?.primary_photo_url && (
                        <img src={fileUrl(l.item.primary_photo_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <Link to={`/app/items/${l.item_id}`} className="hover:text-oxblood-deep truncate block">
                        <span className="font-medium">{l.item?.name || "Item"}</span> · with {l.borrower_name}
                      </Link>
                      <p className="text-[11px] text-oxblood-deep/80">{d.label}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {summary?.total_items === 0 ? (
          <div className="mt-12 border border-border bg-bone/30 p-10 text-center" data-testid="dashboard-empty">
            <p className="font-display text-2xl">Your wardrobe index is empty.</p>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              Start by adding your first item — a gown, a coat, an archive piece. We'll grow with you.
            </p>
            <Button asChild className="mt-6 bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11 px-6" data-testid="dashboard-empty-cta">
              <Link to="/app/items/new"><Plus className="w-4 h-4 mr-2" />Catalog your first item</Link>
            </Button>
          </div>
        ) : (
          <div className="mt-14 grid lg:grid-cols-3 gap-10">
            <section className="lg:col-span-2" data-testid="dashboard-recent-items">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="font-display text-xl">Recently added</h2>
                <Link to="/app/items" className="text-xs text-oxblood hover:underline underline-offset-4">View all →</Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {recent === null
                  ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-[4/5]" />)
                  : recent.map((it) => (
                    <Link key={it.id} to={`/app/items/${it.id}`} className="group block bg-background border border-border hover:border-oxblood/40">
                      <div className="aspect-[4/5] bg-bone overflow-hidden">
                        {it.primary_photo_url ? (
                          <img src={fileUrl(it.primary_photo_url)} alt={it.name} loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                        ) : <div className="w-full h-full grid place-items-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">No photo</div>}
                      </div>
                      <div className="p-3">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">{it.brand || it.category}</p>
                        <p className="font-display text-sm leading-snug line-clamp-2 mt-1">{it.name}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </section>
            <section data-testid="dashboard-activity">
              <h2 className="font-display text-xl mb-5">Recent activity</h2>
              <ul className="space-y-3">
                {activity === null
                  ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16" />)
                  : activity.length === 0
                  ? <li className="text-sm text-muted-foreground">No movements yet.</li>
                  : activity.map((a) => (
                    <li key={a.id} className="border-l-2 border-oxblood/40 pl-3 text-sm">
                      <p className="leading-snug">
                        <span className="font-medium">{a.item_name || "Item"}</span>{" "}
                        <span className="text-muted-foreground">moved</span>
                        {a.from_location_name && <> <span className="text-muted-foreground">from</span> {a.from_location_name}</>}
                        <> <ArrowRight className="inline w-3 h-3 -mt-0.5 mx-0.5" /> {a.to_location_name || "—"}</>
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {timeAgo(a.moved_at)}{a.moved_by_name ? ` · ${a.moved_by_name}` : ""}{a.reason ? ` · ${a.reason}` : ""}
                      </p>
                    </li>
                  ))}
              </ul>
            </section>
          </div>
        )}
      </div>
    </main>
  );
}
